import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SeoMeta, SchemaOrg } from '@/components/base/SeoHead';
import { siteInfo } from '@/mocks/matches';
import { faqData } from '@/mocks/articles';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: `${siteInfo.name}品牌介绍、平台信誉及联系方式`,
    url: `${siteInfo.url}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
      description: siteInfo.description,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      foundingDate: siteInfo.foundingYear,
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteInfo.address,
        addressLocality: '北京',
        addressCountry: 'CN',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteInfo.url}/about` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <SeoMeta
        title={`关于我们 - ${siteInfo.name}`}
        description={`${siteInfo.name}品牌介绍 - ${siteInfo.description} 了解我们的平台信誉与联系方式。`}
        keywords={`${siteInfo.keywords},关于真人体育,真人体育品牌,真人体育信誉,真人体育联系方式`}
        canonicalUrl={`${siteInfo.url}/about`}
      />
      <SchemaOrg schema={aboutSchema} />
      <SchemaOrg schema={breadcrumbSchema} />
      <SchemaOrg schema={faqSchema} />

      <Navbar />

      <main className="pt-16 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-10 md:py-14">
            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
                关于 {siteInfo.name}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
                专注体育赛事竞猜服务，致力于为全球体育爱好者提供安全、专业、透明的竞猜体验
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>品牌介绍</h2>
                <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    <strong>{siteInfo.name}</strong>成立于{siteInfo.foundingYear}年，是国内领先的体育赛事竞猜服务平台。
                    平台汇聚全球100+顶级体育赛事，覆盖足球、篮球、网球、电子竞技等多个热门运动项目，
                    为体育爱好者提供实时赔率、专业数据分析和安全可靠的竞猜环境。
                  </p>
                  <p>
                    作为行业领先者，真人体育始终秉持"公平、公正、公开"的运营理念，
                    所有赛事赔率实时更新、公开透明，确保每位用户都能在公平的环境中进行竞猜。
                    我们拥有专业的赛事分析团队，为用户提供高质量的赛事数据分析和竞猜建议。
                  </p>
                  <p>
                    真人体育平台采用银行级加密技术保护用户信息和资金安全，建立完善的客户服务系统，
                    提供7×24小时在线客服支持。截至目前，平台已累计服务超过500万用户，
                    赢得了广大体育爱好者的信赖与好评。
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>平台信誉</h2>
                <div className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    <strong>合规运营：</strong>
                    真人体育持有合法运营资质，严格遵守国家相关法律法规，确保平台运营的合规性和可持续性。
                  </p>
                  <p>
                    <strong>资金保障：</strong>
                    平台采用独立的资金存管体系，用户资金与平台运营资金完全分离，确保用户资金安全。
                  </p>
                  <p>
                    <strong>赔率透明：</strong>
                    所有赛事的赔率数据实时公开，用户可以随时查看赔率变化趋势，确保竞猜过程的公平透明。
                  </p>
                  <p>
                    <strong>数据安全：</strong>
                    平台通过信息安全等级保护认证，采用多层加密技术保护用户隐私数据，防止信息泄露。
                  </p>
                  <p>
                    <strong>专业团队：</strong>
                    真人体育拥有超过50人的专业赛事分析师团队，覆盖各大体育项目，为用户提供权威的竞猜参考。
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-14">
              <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                <p className="text-2xl md:text-3xl font-bold text-primary-500">{siteInfo.foundingYear}</p>
                <p className="text-xs text-foreground-500 mt-1">创始年份</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                <p className="text-2xl md:text-3xl font-bold text-primary-500">100+</p>
                <p className="text-xs text-foreground-500 mt-1">赛事覆盖</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                <p className="text-2xl md:text-3xl font-bold text-primary-500">500万+</p>
                <p className="text-xs text-foreground-500 mt-1">活跃用户</p>
              </div>
              <div className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                <p className="text-2xl md:text-3xl font-bold text-primary-500">99.9%</p>
                <p className="text-xs text-foreground-500 mt-1">安全率</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-14">
              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>联系我们</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">客服热线</p>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                      <p className="text-xs text-foreground-400">7×24小时在线服务</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                      <p className="text-xs text-foreground-400">工作日24小时内回复</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">公司地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>常见问题</h2>
                <div className="space-y-3">
                  {faqData.slice(0, 4).map((faq, index) => (
                    <details key={index} className="group bg-background-100 rounded-lg border border-background-200/70">
                      <summary className="flex items-center justify-between p-4 cursor-pointer text-sm font-medium text-foreground-950 list-none">
                        <span>{faq.question}</span>
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-4 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>

        <section className="py-12 bg-primary-500">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl font-bold text-background-50 mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              加入真人体育，开启您的竞猜之旅
            </h2>
            <p className="text-background-50/80 text-sm mb-6">
              500万+用户的信赖之选，立即体验最专业的赛事竞猜服务
            </p>
            <Link
              to="/category/football"
              className="inline-flex px-8 py-3 bg-background-50 text-primary-500 font-semibold rounded-lg hover:bg-background-100 transition-all text-sm no-underline whitespace-nowrap"
            >
              立即开始竞猜
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}